// Shared label dictionaries for Dough Recipes (docs/RECIPE_MODULE.md Phase 2) — used by both
// DoughRecipeEditorPage.js and DoughRecipeRunPage.js so the two stay in sync.
export const YEAST_TYPE_OPTIONS = [
  { value: 'fresh', label: 'Fresh yeast' },
  { value: 'dry', label: 'Active dry yeast' },
  { value: 'instant', label: 'Instant yeast' },
  { value: 'sourdough_starter', label: 'Mother Dough' },
];

// Typical defaults per pre-ferment type, prefilled when the user picks one — not enforced, just a
// sane starting point the user can adjust with the sliders. Autolyse defaults to 100% of the
// flour at a stiffer 55% hydration (the Raymond Calvel method), rather than a split-off portion
// at the dough's own full hydration like the other 5 types.
export const PRE_FERMENT_DEFAULTS = {
  biga: { flourPercent: 70, hydrationPercent: 50 },
  poolish: { flourPercent: 30, hydrationPercent: 100 },
  levain: { flourPercent: 20, hydrationPercent: 100 },
  sourdough: { flourPercent: 20, hydrationPercent: 100 },
  sponge: { flourPercent: 40, hydrationPercent: 60 },
  autolyse: { flourPercent: 100, hydrationPercent: 55 },
};
export const PRE_FERMENT_LABELS = {
  none: 'None',
  biga: 'Biga',
  poolish: 'Poolish',
  levain: 'Levain',
  sourdough: 'Sourdough starter',
  sponge: 'Sponge',
  autolyse: 'Autolyse',
};

// Autolyse carries no leavening agent — it's a plain flour+water rest done BEFORE salt/yeast are
// mixed in, unlike the other 5 types, which are true pre-ferments that carry 100% of the yeast
// into their own stage. Checked by both the backend (computeDoughBreakdown()) and the frontend
// (editor preview + run page breakdown) so yeast lands in the right stage either way.
export const PRE_FERMENT_CARRIES_YEAST = {
  none: false,
  biga: true,
  poolish: true,
  levain: true,
  sourdough: true,
  sponge: true,
  autolyse: false,
};

// Shown next to the pre-ferment picker so a baker unfamiliar with a method (or a novice client)
// knows what it actually is and how it's done, not just its name.
export const PRE_FERMENT_DESCRIPTIONS = {
  none: 'No pre-ferment — flour, water, salt, and yeast are all mixed together in one straight dough.',
  biga: 'A stiff, low-hydration Italian pre-ferment built with a small amount of yeast and left to ferment slowly — usually 12–16 hours at cool room temperature — before being mixed into the final dough. Builds strength and flavor with minimal sourness.',
  poolish: 'A wet, 100%-hydration French pre-ferment built with a small amount of yeast and left to ferment 8–16 hours. Produces a lighter, more extensible dough with a mild, slightly tangy flavor.',
  levain: 'A pre-ferment built from an active sourdough culture instead of commercial yeast, fermented several hours to overnight. Adds real sourdough character and complexity without being a full wild-fermentation loaf.',
  sourdough: 'A mature sourdough starter used directly as the only leavening — no commercial yeast at all. Longer and more variable fermentation, with the most pronounced sour flavor of the five.',
  sponge: "A soft pre-ferment mixed closer to the final dough's own hydration, built with yeast and rested just a few hours. Speeds up bulk fermentation and improves texture with less planning than a biga or poolish.",
  autolyse: "Just flour and water — no salt, no yeast — mixed and rested (typically 20–60 minutes) before the rest of the ingredients are added. Lets enzymes begin developing gluten on their own, cutting mixing time and improving extensibility. This stiffer, 55%-hydration version (rather than mixing at the dough's full hydration) is the method popularized by French master baker Raymond Calvel.",
};
